import React, { useState, useEffect, useMemo } from 'react';
import { Timestamp, doc, deleteDoc, collection, query, where, getDocs, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import '../../styles/staff-forms-modals.css';

const AdminClockOutModal = ({ 
  isOpen, 
  onClose, 
  activeSession,
  companyId, 
  staffId, 
  staffName,
  departmentName,
  onSuccess 
}) => {
  // Form state for clock out
  const [formData, setFormData] = useState({
    clockOutTime: '',
    adjustClockIn: '',
    adjustBreakStart: '',
    adjustBreakEnd: '',
    note: '',
    forceClockOut: true
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen && activeSession) {
      const now = new Date();
      const clockInTime = activeSession.clockIn || now;
      const breakStart = activeSession.onBreak ? activeSession.breakStart : null;
      
      setFormData({
        clockOutTime: now.toISOString().slice(0, 16),
        adjustClockIn: clockInTime.toISOString().slice(0, 16),
        adjustBreakStart: breakStart ? breakStart.toISOString().slice(0, 16) : '',
        adjustBreakEnd: breakStart ? now.toISOString().slice(0, 16) : '',
        note: '',
        forceClockOut: true
      });
      
      setError(null);
      setValidationErrors({});
      setShowAdvancedOptions(false);
    }
  }, [isOpen, activeSession]);

  // Calculate durations in real-time
  const calculatedDurations = useMemo(() => {
    if (!activeSession || !formData.clockOutTime || !formData.adjustClockIn) {
      return { workDuration: 0, breakDuration: 0, totalDuration: 0 };
    }

    return calculateSessionDurations(formData, activeSession);
  }, [formData, activeSession]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.clockOutTime) {
      errors.clockOutTime = 'Clock out time is required';
    }
    
    if (!formData.adjustClockIn) {
      errors.adjustClockIn = 'Clock in time is required';
    }
    
    // Validate times are valid dates
    if (formData.clockOutTime && !isValidTimestamp(formData.clockOutTime)) {
      errors.clockOutTime = 'Invalid clock out time';
    }
    
    if (formData.adjustClockIn && !isValidTimestamp(formData.adjustClockIn)) {
      errors.adjustClockIn = 'Invalid clock in time';
    }
    
    // Validate chronological order
    if (formData.clockOutTime && formData.adjustClockIn) {
      const clockOut = new Date(formData.clockOutTime);
      const clockIn = new Date(formData.adjustClockIn);
      
      if (clockOut <= clockIn) {
        errors.clockOutTime = 'Clock out must be after clock in';
      }
    }
    
    // Validate break times if provided
    if (formData.adjustBreakStart && formData.adjustBreakEnd) {
      const breakStart = new Date(formData.adjustBreakStart);
      const breakEnd = new Date(formData.adjustBreakEnd);
      const clockIn = new Date(formData.adjustClockIn);
      const clockOut = new Date(formData.clockOutTime);
      
      if (breakEnd <= breakStart) {
        errors.adjustBreakEnd = 'Break end must be after break start';
      }
      
      if (breakStart < clockIn) {
        errors.adjustBreakStart = 'Break start must be after clock in';
      }
      
      if (breakEnd > clockOut) {
        errors.adjustBreakEnd = 'Break end must be before clock out';
      }
    }
    
    // If only one break time is provided, require both
    if ((formData.adjustBreakStart && !formData.adjustBreakEnd) || 
        (!formData.adjustBreakStart && formData.adjustBreakEnd)) {
      errors.adjustBreakStart = 'Both break start and end times are required';
      errors.adjustBreakEnd = 'Both break start and end times are required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle admin clock out
  const handleClockOut = async () => {
    if (!validateForm()) {
      setError('Please fix the validation errors before proceeding.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create timestamps
      const clockInTimestamp = Timestamp.fromDate(new Date(formData.adjustClockIn));
      const clockOutTimestamp = Timestamp.fromDate(new Date(formData.clockOutTime));
      
      let breakStartTimestamp = null;
      let breakEndTimestamp = null;
      
      if (formData.adjustBreakStart && formData.adjustBreakEnd) {
        breakStartTimestamp = Timestamp.fromDate(new Date(formData.adjustBreakStart));
        breakEndTimestamp = Timestamp.fromDate(new Date(formData.adjustBreakEnd));
      }

      // Get today's date string
      const dateFormatter = new Date(formData.clockOutTime);
      const dateString = formatDateString(dateFormatter);

      // Step 1: Find or create time entry for today
      const timeEntriesRef = collection(db, 'companies', companyId, 'timeEntries', staffId, 'entries');
      const todayQuery = query(timeEntriesRef, where('date', '==', dateString));
      const todaySnapshot = await getDocs(todayQuery);

      let timeEntryRef;
      let existingEntry = null;

      if (todaySnapshot.empty) {
        // Create new time entry
        timeEntryRef = doc(timeEntriesRef);
      } else {
        // Update existing time entry
        const existingDoc = todaySnapshot.docs[0];
        timeEntryRef = existingDoc.ref;
        existingEntry = existingDoc.data();
      }

      // Calculate accumulated durations
      const currentWorkDuration = existingEntry?.totalWorkDuration || 0;
      const currentBreakDuration = existingEntry?.totalBreakDuration || 0;
      
      // Add this session's durations
      const newWorkDuration = currentWorkDuration + calculatedDurations.workDuration;
      const newBreakDuration = currentBreakDuration + calculatedDurations.breakDuration;

      // Prepare timestamp arrays
      const clockIns = existingEntry?.clockIns || [];
      clockIns.push(clockInTimestamp);
      
      const clockOuts = existingEntry?.clockOuts || [];
      clockOuts.push(clockOutTimestamp);
      
      const breakStarts = existingEntry?.breakStarts || [];
      const breakEnds = existingEntry?.breakEnds || [];
      
      if (breakStartTimestamp && breakEndTimestamp) {
        breakStarts.push(breakStartTimestamp);
        breakEnds.push(breakEndTimestamp);
      }

      // Prepare time entry data
      const timeEntryData = {
        departmentId: activeSession.departmentId,
        date: dateString,
        clockIns,
        clockOuts,
        breakStarts,
        breakEnds,
        totalWorkDuration: newWorkDuration,
        totalBreakDuration: newBreakDuration,
        sessionStatus: 'Clocked Out',
        deviceType: activeSession.deviceType || 'Web Admin',
        deviceId: 'admin-web-interface',
        note: formData.note.trim(),
        lastModified: Timestamp.now(),
        modifiedBy: 'admin'
      };

      // Step 2: Update/create time entry
      if (existingEntry) {
        await updateDoc(timeEntryRef, timeEntryData);
      } else {
        await setDoc(timeEntryRef, timeEntryData);
      }

      // Step 3: Delete active session
      const activeSessionRef = doc(db, 'companies', companyId, 'activeSessions', staffId);
      await deleteDoc(activeSessionRef);

      // Success callback
      if (onSuccess) {
        await onSuccess({
          action: 'admin_clock_out',
          staffId,
          staffName,
          clockOutTime: formData.clockOutTime,
          workDuration: calculatedDurations.workDuration,
          breakDuration: calculatedDurations.breakDuration,
          note: formData.note
        });
      }

      onClose();
    } catch (err) {
      console.error('Error during admin clock out:', err);
      setError('Failed to clock out staff member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (formData.note.trim() || showAdvancedOptions) {
      if (window.confirm('Are you sure you want to cancel? Any changes will be lost.')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen || !activeSession) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content admin-clock-out-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <h2>Admin Clock Out</h2>
            <p className="modal-subtitle">
              <span className="staff-name">{staffName}</span>
              <span className="separator">•</span>
              <span className="department">{departmentName}</span>
              <span className="separator">•</span>
              <span className={`status ${activeSession.onBreak ? 'on-break' : 'working'}`}>
                {activeSession.onBreak ? 'Currently on Break' : 'Currently Working'}
              </span>
            </p>
          </div>
          <button className="modal-close" onClick={handleCancel} title="Close">
            <span className="icon-close">×</span>
          </button>
        </div>

        {error && (
          <div className="error-message modal-error">
            <span className="icon-error">⚠</span>
            {error}
          </div>
        )}

        <div className="modal-body">
          {/* Current Session Info */}
          <div className="session-summary">
            <h3>Current Session Summary</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="label">Clock In Time:</span>
                <span className="value">{formatTime(activeSession.clockIn)}</span>
              </div>
              <div className="summary-item">
                <span className="label">Current Duration:</span>
                <span className="value duration">{formatDuration(Math.floor((new Date() - activeSession.clockIn) / 60000))}</span>
              </div>
              {activeSession.onBreak && (
                <>
                  <div className="summary-item">
                    <span className="label">Break Started:</span>
                    <span className="value">{formatTime(activeSession.breakStart)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Break Duration:</span>
                    <span className="value break-duration">
                      {formatDuration(Math.floor((new Date() - activeSession.breakStart) / 60000))}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Clock Out Time */}
          <div className="form-section">
            <h3>Clock Out Time</h3>
            <div className="time-input-group">
              <label htmlFor="clockOutTime">Set clock out time:</label>
              <input
                id="clockOutTime"
                type="datetime-local"
                value={formData.clockOutTime}
                onChange={(e) => handleInputChange('clockOutTime', e.target.value)}
                className={validationErrors.clockOutTime ? 'error' : ''}
              />
              {validationErrors.clockOutTime && (
                <div className="field-error">{validationErrors.clockOutTime}</div>
              )}
              <button 
                type="button"
                onClick={() => handleInputChange('clockOutTime', new Date().toISOString().slice(0, 16))}
                className="set-now-btn"
              >
                Set to Now
              </button>
            </div>
          </div>

          {/* Calculated Durations Preview */}
          <div className="duration-preview">
            <h3>Final Durations</h3>
            <div className="duration-summary">
              <div className="duration-item work">
                <div className="duration-value">{formatDurationDisplay(calculatedDurations.workDuration)}</div>
                <div className="duration-label">Work Time</div>
              </div>
              <div className="duration-item break">
                <div className="duration-value">{formatDurationDisplay(calculatedDurations.breakDuration)}</div>
                <div className="duration-label">Break Time</div>
              </div>
              <div className="duration-item total">
                <div className="duration-value">{formatDurationDisplay(calculatedDurations.totalDuration)}</div>
                <div className="duration-label">Total Time</div>
              </div>
            </div>
          </div>

          {/* Advanced Time Adjustments */}
          <div className="advanced-section">
            <button 
              type="button"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              className="toggle-advanced"
            >
              <span className={`icon-chevron ${showAdvancedOptions ? 'up' : 'down'}`}>
                {showAdvancedOptions ? '▲' : '▼'}
              </span>
              Advanced Time Adjustments
            </button>

            {showAdvancedOptions && (
              <div className="advanced-options">
                <div className="form-section">
                  <h4>Adjust Clock In Time</h4>
                  <input
                    type="datetime-local"
                    value={formData.adjustClockIn}
                    onChange={(e) => handleInputChange('adjustClockIn', e.target.value)}
                    className={validationErrors.adjustClockIn ? 'error' : ''}
                  />
                  {validationErrors.adjustClockIn && (
                    <div className="field-error">{validationErrors.adjustClockIn}</div>
                  )}
                </div>

                {activeSession.onBreak && (
                  <div className="form-section">
                    <h4>Adjust Break Times</h4>
                    <div className="break-time-inputs">
                      <div className="break-input">
                        <label>Break Start:</label>
                        <input
                          type="datetime-local"
                          value={formData.adjustBreakStart}
                          onChange={(e) => handleInputChange('adjustBreakStart', e.target.value)}
                          className={validationErrors.adjustBreakStart ? 'error' : ''}
                        />
                        {validationErrors.adjustBreakStart && (
                          <div className="field-error">{validationErrors.adjustBreakStart}</div>
                        )}
                      </div>
                      <div className="break-input">
                        <label>Break End:</label>
                        <input
                          type="datetime-local"
                          value={formData.adjustBreakEnd}
                          onChange={(e) => handleInputChange('adjustBreakEnd', e.target.value)}
                          className={validationErrors.adjustBreakEnd ? 'error' : ''}
                        />
                        {validationErrors.adjustBreakEnd && (
                          <div className="field-error">{validationErrors.adjustBreakEnd}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Admin Notes */}
          <div className="form-section">
            <h3>
              <span className="icon-note">📝</span>
              Admin Notes
            </h3>
            <textarea
              value={formData.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              placeholder="Add notes about this admin clock out (optional)..."
              rows={3}
              className="note-textarea"
              maxLength={500}
            />
            <div className="character-count">
              {formData.note.length}/500 characters
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="footer-warning">
            <span className="icon-warning">⚠</span>
            This will immediately clock out the staff member and cannot be undone.
          </div>
          <div className="footer-actions">
            <button 
              onClick={handleCancel} 
              className="cancel-button"
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              onClick={handleClockOut} 
              className="clock-out-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner small"></span>
                  Processing...
                </>
              ) : (
                <>
                  <span className="icon-stop">⏹</span>
                  Clock Out Now
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const formatDateString = (date) => {
  return date.getFullYear() + '-' + 
         String(date.getMonth() + 1).padStart(2, '0') + '-' + 
         String(date.getDate()).padStart(2, '0');
};

const formatTime = (date) => {
  if (!date) return '';
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false
  });
};

const formatDuration = (minutes) => {
  if (!minutes || minutes < 0) return '0:00';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours}:${String(mins).padStart(2, '0')}`;
};

const formatDurationDisplay = (seconds) => {
  if (!seconds || seconds < 0) return '0h 0m';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const isValidTimestamp = (timestamp) => {
  if (!timestamp) return false;
  const date = new Date(timestamp);
  return date instanceof Date && !isNaN(date);
};

// Duration calculation matching iOS business logic
const calculateSessionDurations = (formData, activeSession) => {
  try {
    const clockIn = new Date(formData.adjustClockIn);
    const clockOut = new Date(formData.clockOutTime);
    
    if (isNaN(clockIn) || isNaN(clockOut)) {
      return { workDuration: 0, breakDuration: 0, totalDuration: 0 };
    }
    
    const totalSessionDuration = (clockOut - clockIn) / 1000; // in seconds
    let breakDuration = 0;
    
    // Calculate break duration if break times are provided
    if (formData.adjustBreakStart && formData.adjustBreakEnd) {
      const breakStart = new Date(formData.adjustBreakStart);
      const breakEnd = new Date(formData.adjustBreakEnd);
      
      if (!isNaN(breakStart) && !isNaN(breakEnd) && breakEnd > breakStart) {
        breakDuration = (breakEnd - breakStart) / 1000; // in seconds
      }
    }
    
    const workDuration = Math.max(0, totalSessionDuration - breakDuration);
    
    return {
      workDuration: Math.round(workDuration),
      breakDuration: Math.round(breakDuration),
      totalDuration: Math.round(totalSessionDuration)
    };
  } catch (error) {
    console.error('Error calculating session durations:', error);
    return { workDuration: 0, breakDuration: 0, totalDuration: 0 };
  }
};

export default AdminClockOutModal;